import React, { useEffect, useState, useRef, Fragment } from 'react';
import { Search, Download, Video, MessageSquare, Mic, AlertTriangle, Clock, CheckCircle2, XCircle, MoreVertical, Eye, UserX, FileText, PlayCircle, Star, Zap, Activity, Ban, Undo2, MessageCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Tabs } from '../ui/Tabs';
import { Pagination } from '../ui/Pagination';
import { VideoCallModal } from '../ui/VideoCallModal';
// --- Fixed-position Tooltip (escapes overflow containers) ---
function Tooltip({
  children,
  text
}: {
  children: React.ReactNode;
  text: string;
}) {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({
    top: 0,
    left: 0
  });
  const ref = useRef<HTMLSpanElement>(null);
  const handleEnter = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setPos({
        top: rect.top - 4,
        left: rect.left + rect.width / 2
      });
    }
    setShow(true);
  };
  return <span ref={ref} className="relative inline-flex" onMouseEnter={handleEnter} onMouseLeave={() => setShow(false)} data-id="element-2654">
      {children}
      {show && <span className="fixed px-2.5 py-1.5 text-[10px] font-medium text-white bg-slate-800 rounded-lg whitespace-nowrap z-[9999] shadow-xl pointer-events-none -translate-x-1/2 -translate-y-full" style={{
      top: pos.top,
      left: pos.left
    }} data-id="element-2655">
          {text}
          <span className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-800" data-id="element-2656" />
        </span>}
    </span>;
}
interface AdminConsultationsManagerProps {
  consultations: any[];
  users: any[];
  astrologers: any[];
  setActiveTab: (tab: string) => void;
  setDetailPanel: (panel: any) => void;
  success: (msg: string) => void;
}
export function AdminConsultationsManager({
  consultations,
  users,
  astrologers,
  setActiveTab,
  setDetailPanel,
  success
}: AdminConsultationsManagerProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    format: 'all',
    riskFlag: 'all',
    date: 'all',
    complaints: 'all'
  });
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [activeDetailTab, setActiveDetailTab] = useState('info');
  const [openActionMenuId, setOpenActionMenuId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showCallModal, setShowCallModal] = useState(false);
  const [callMode, setCallMode] = useState<'video' | 'audio'>('video');
  const [callParticipant, setCallParticipant] = useState({
    name: '',
    initials: ''
  });
  const itemsPerPage = 15;
  // --- MOCK DATA ENRICHMENT ---
  const baseConsultations = consultations.length > 0 ? consultations : [{
    id: 'CON-401',
    user: 'Анна Петрова',
    astrologer: 'Мария Звёздная',
    format: 'video',
    date: '15.03.2026 14:00',
    status: 'completed',
    priceNum: 7500
  }, {
    id: 'CON-402',
    user: 'Иван Сидоров',
    astrologer: 'Алексей Лунный',
    format: 'chat',
    date: '15.03.2026 15:30',
    status: 'active',
    priceNum: 3500
  }, {
    id: 'CON-403',
    user: 'Maria Schmidt',
    astrologer: 'Ольга Солнцева',
    format: 'audio',
    date: '15.03.2026 16:00',
    status: 'scheduled',
    priceNum: 5000
  }, {
    id: 'CON-404',
    user: 'Олег Козлов',
    astrologer: 'Дмитрий Космос',
    format: 'video',
    date: '14.03.2026 11:00',
    status: 'dispute',
    priceNum: 3500
  }, {
    id: 'CON-405',
    user: 'Елена Волкова',
    astrologer: 'Мария Звёздная',
    format: 'video',
    date: '14.03.2026 10:00',
    status: 'completed',
    priceNum: 12000
  }, {
    id: 'CON-406',
    user: 'Pierre Dupont',
    astrologer: 'Алексей Лунный',
    format: 'chat',
    date: '13.03.2026 18:00',
    status: 'cancelled',
    priceNum: 3500
  }, {
    id: 'CON-407',
    user: 'Ольга Иванова',
    astrologer: 'Дмитрий Космос',
    format: 'video',
    date: '15.03.2026 16:30',
    status: 'waiting',
    priceNum: 5000
  }];
  const enrichedConsultations = baseConsultations.map((c, i) => {
    const isToday = c.date.includes('15.03.2026');
    const hasComplaint = i === 3 || i === 5;
    const isOnlineNow = c.status === 'active' || c.status === 'waiting';
    // Risk flags
    const riskFlags = [];
    if (i === 3) riskFlags.push({
      type: 'dispute',
      label: 'Спор'
    });
    if (i === 5) riskFlags.push({
      type: 'contacts',
      label: 'Передача контактов'
    });
    if (hasComplaint) riskFlags.push({
      type: 'complaint',
      label: 'Жалоба'
    });
    if (c.status === 'cancelled') riskFlags.push({
      type: 'cancelled',
      label: 'Отмена'
    });
    // Durations
    const plannedDuration = c.format === 'chat' ? 30 : 60;
    const actualDuration = c.status === 'completed' ? plannedDuration + (i % 3 === 0 ? 15 : -5) : c.status === 'active' ? 12 : 0;
    // Last message
    const lastMessages = ['клиент написал 3 мин назад', 'астролог отправил файл', 'ожидание ответа астролога', 'чат завершен', 'звонок идет (12:04)'];
    const lastMessage = c.status === 'completed' ? 'чат завершен' : lastMessages[i % lastMessages.length];
    // Rating
    const rating = c.status === 'completed' ? i % 2 === 0 ? 5.0 : 4.2 : null;
    // Commission
    const commissionRate = 0.2;
    const commission = c.priceNum * commissionRate;
    return {
      ...c,
      isToday,
      hasComplaint,
      isOnlineNow,
      riskFlags,
      hasRisk: riskFlags.length > 0,
      plannedDuration,
      actualDuration,
      lastMessage,
      rating,
      commission,
      commissionRate,
      hasRecording: c.format !== 'chat' && (c.status === 'completed' || c.status === 'dispute'),
      timeline: [{
        time: '14:00',
        text: 'Клиент вошёл в комнату',
        type: 'user'
      }, {
        time: '14:02',
        text: 'Астролог подключился',
        type: 'astrologer'
      }, {
        time: '14:03',
        text: 'Консультация началась',
        type: 'system'
      }, ...(c.status === 'completed' ? [{
        time: '14:45',
        text: 'Консультация завершена',
        type: 'system'
      }] : []), ...(hasComplaint ? [{
        time: '15:10',
        text: 'Клиент оставил жалобу',
        type: 'alert'
      }] : [])]
    };
  });
  // KPI Calculations
  const totalCount = enrichedConsultations.length;
  const todayCount = enrichedConsultations.filter(c => c.isToday).length;
  const activeCount = enrichedConsultations.filter(c => c.status === 'active').length;
  const complaintCount = enrichedConsultations.filter(c => c.hasComplaint).length;
  const riskCount = enrichedConsultations.filter(c => c.hasRisk).length;
  const renderKpiCard = (label: string, value: string | number, icon: React.ReactNode, bgClass: string, textClass: string, filterKey: string, filterValue: string) => {
    const isActive = filters[filterKey as keyof typeof filters] === filterValue;
    return <Card padding="sm" hoverable className={`rounded-xl shadow-none border-slate-200 p-4 flex items-center gap-4 cursor-pointer transition-all ${isActive ? 'ring-2 ring-indigo-500 bg-indigo-50/30' : ''}`} onClick={() => {
      // Reset all filters first, then apply the selected one
      const newFilters = {
        status: 'all',
        format: 'all',
        riskFlag: 'all',
        date: 'all',
        complaints: 'all'
      };
      if (!isActive) {
        newFilters[filterKey as keyof typeof newFilters] = filterValue;
      }
      setFilters(newFilters);
    }} data-id="element-2657">
        <div className={`w-10 h-10 rounded-xl ${bgClass} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`} data-id="element-2658">
          {icon}
        </div>
        <div data-id="element-2659">
          <div className="text-xs text-slate-500 font-medium mb-0.5" data-id="element-2660">
            {label}
          </div>
          <div className={`text-xl font-bold ${textClass}`} data-id="element-2661">{value}</div>
        </div>
      </Card>;
  };
  const getFormatIcon = (format: string) => {
    switch (format) {
      case 'video':
        return <Video className="w-3.5 h-3.5 text-blue-500" data-id="element-2662" />;
      case 'audio':
        return <Mic className="w-3.5 h-3.5 text-purple-500" data-id="element-2663" />;
      default:
        return <MessageSquare className="w-3.5 h-3.5 text-emerald-500" data-id="element-2664" />;
    }
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="success" size="sm" className="text-[9px]" data-id="element-2665">
            Активна
          </Badge>;
      case 'waiting':
        return <Badge variant="warning" size="sm" className="text-[9px] animate-pulse" data-id="element-2666">
            Ожидает астролога
          </Badge>;
      case 'scheduled':
        return <Badge variant="neutral" size="sm" className="text-[9px]" data-id="element-2667">
            Запланирована
          </Badge>;
      case 'completed':
        return <Badge variant="neutral" size="sm" className="text-[9px]" data-id="element-2668">
            Завершена
          </Badge>;
      case 'cancelled':
        return <Badge variant="error" size="sm" className="text-[9px]" data-id="element-2669">
            Отменена
          </Badge>;
      case 'dispute':
        return <Badge variant="warning" size="sm" className="text-[9px]" data-id="element-2670">
            Спор
          </Badge>;
      default:
        return <Badge variant="neutral" size="sm" className="text-[9px]" data-id="element-2671">
            {status}
          </Badge>;
    }
  };
  const handleExport = () => {
    success(`Экспорт ${filteredConsultations.length} консультаций завершён`);
  };
  const filteredConsultations = enrichedConsultations.filter(c => {
    const matchesSearch = c.id.toLowerCase().includes(searchQuery.toLowerCase()) || c.user.toLowerCase().includes(searchQuery.toLowerCase()) || c.astrologer.toLowerCase().includes(searchQuery.toLowerCase());
    if (!matchesSearch) return false;
    if (filters.status !== 'all') {
      if (filters.status === 'online' && !c.isOnlineNow) return false;
      if (filters.status !== 'online' && c.status !== filters.status) return false;
    }
    if (filters.format !== 'all' && c.format !== filters.format) return false;
    if (filters.riskFlag !== 'all') {
      if (filters.riskFlag === 'true' && !c.hasRisk) return false;
      if (filters.riskFlag === 'false' && c.hasRisk) return false;
    }
    if (filters.date === 'today' && !c.isToday) return false;
    if (filters.complaints === 'true' && !c.hasComplaint) return false;
    return true;
  });
  const totalPages = Math.ceil(filteredConsultations.length / itemsPerPage);
  const currentConsultations = filteredConsultations.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  useEffect(() => {
    const handleClickOutside = () => setOpenActionMenuId(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);
  const renderDetailTabs = (consultation: any) => {
    return <div className="p-6 bg-slate-50/50 border-b border-slate-200 shadow-inner" data-id="element-2672">
        <div className="flex justify-between items-start mb-6" data-id="element-2673">
          <div data-id="element-2674">
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2" data-id="element-2675">
              Консультация {consultation.id}
              {consultation.riskFlags.map((rf: any, idx: number) => <Badge key={idx} variant="error" size="sm" className="text-[10px]" data-id="element-2676">
                  <AlertTriangle className="w-3 h-3 mr-1" data-id="element-2677" /> {rf.label}
                </Badge>)}
            </h3>
            <p className="text-sm text-slate-500 mt-1" data-id="element-2678">
              {consultation.date} • План: {consultation.plannedDuration} мин /
              Факт: {consultation.actualDuration} мин
            </p>
          </div>
          <div className="flex gap-2" data-id="element-2679">
            {(consultation.status === 'active' || consultation.status === 'waiting') && <Button variant="primary" size="sm" icon={<Video className="w-4 h-4" data-id="element-2681" />} onClick={() => {
            setCallParticipant({
              name: consultation.astrologer,
              initials: consultation.astrologer.charAt(0)
            });
            setCallMode(consultation.format === 'audio' ? 'audio' : 'video');
            setShowCallModal(true);
          }} data-id="element-2680">
                Присоединиться к звонку
              </Button>}
            <Button variant="outline" size="sm" icon={<MessageCircle className="w-4 h-4" data-id="element-2683" />} onClick={() => success('Чат открыт')} data-id="element-2682">
              Открыть чат
            </Button>
            <Button variant="outline" size="sm" icon={<AlertTriangle className="w-4 h-4" data-id="element-2685" />} className="text-amber-600 border-amber-200 hover:bg-amber-50" onClick={() => success('Предупреждение отправлено')} data-id="element-2684">
              Предупредить
            </Button>
          </div>
        </div>

        <Tabs tabs={[{
        id: 'info',
        label: 'Информация'
      }, {
        id: 'chat',
        label: 'Чат'
      }, {
        id: 'video',
        label: 'Запись'
      }, {
        id: 'ai',
        label: 'AI Анализ'
      }, {
        id: 'history',
        label: 'Таймлайн'
      }]} activeTab={activeDetailTab} onChange={setActiveDetailTab} className="mb-6" data-id="element-2686" />

        {activeDetailTab === 'info' && <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-2687">
            <Card padding="sm" className="border-slate-200 bg-white" data-id="element-2688">
              <div className="text-xs text-slate-500 mb-1" data-id="element-2689">Пользователь</div>
              <div className="font-medium text-sm cursor-pointer hover:text-indigo-600" onClick={() => {
            const match = users.find(u => u.name === consultation.user);
            if (match) setDetailPanel({
              type: 'user_full',
              title: 'Профиль',
              data: match
            });
          }} data-id="element-2690">
                {consultation.user}
              </div>
            </Card>
            <Card padding="sm" className="border-slate-200 bg-white" data-id="element-2691">
              <div className="text-xs text-slate-500 mb-1" data-id="element-2692">Астролог</div>
              <div className="font-medium text-sm cursor-pointer hover:text-indigo-600" onClick={() => {
            const match = astrologers.find(a => a.name === consultation.astrologer);
            if (match) {
              setDetailPanel({
                type: 'astrologer_profile',
                title: 'Профиль',
                data: match
              });
              setActiveTab('astrologers');
            }
          }} data-id="element-2693">
                {consultation.astrologer}
              </div>
            </Card>
            <Card padding="sm" className="border-slate-200 bg-white" data-id="element-2694">
              <div className="text-xs text-slate-500 mb-1" data-id="element-2695">Стоимость</div>
              <div className="font-bold text-sm text-slate-900" data-id="element-2696">
                {consultation.priceNum.toLocaleString('ru-RU')} ₽
              </div>
              <div className="text-[10px] text-emerald-600" data-id="element-2697">
                Комиссия: {consultation.commission.toLocaleString('ru-RU')} ₽
              </div>
            </Card>
            <Card padding="sm" className="border-slate-200 bg-white" data-id="element-2698">
              <div className="text-xs text-slate-500 mb-1" data-id="element-2699">Рейтинг</div>
              <div className="font-medium text-sm flex items-center gap-1" data-id="element-2700">
                {consultation.rating ? <>
                    <Star className="w-4 h-4 text-amber-400 fill-amber-400" data-id="element-2701" />{' '}
                    {consultation.rating}
                  </> : <span className="text-slate-400" data-id="element-2702">Нет оценки</span>}
              </div>
            </Card>
          </div>}

        {activeDetailTab === 'chat' && <div className="bg-white border border-slate-200 rounded-xl p-4 h-80 overflow-y-auto space-y-4" data-id="element-2703">
            <div className="flex items-center justify-between mb-4 pb-2 border-b border-slate-100" data-id="element-2704">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-2705">
                Переписка
              </div>
            </div>
            {/* Mock chat messages */}
            <div className="flex flex-col items-start" data-id="element-2706">
              <div className="flex items-baseline gap-2 mb-1" data-id="element-2707">
                <span className="text-[10px] font-bold text-slate-500" data-id="element-2708">
                  {consultation.user}
                </span>
                <span className="text-[9px] text-slate-400" data-id="element-2709">14:00</span>
              </div>
              <div className="max-w-[80%] p-3 rounded-2xl text-sm bg-slate-100 text-slate-900 rounded-bl-md" data-id="element-2710">
                Здравствуйте! Подскажите, когда лучше начать новый проект?
              </div>
            </div>
            <div className="flex flex-col items-end" data-id="element-2711">
              <div className="flex items-baseline gap-2 mb-1" data-id="element-2712">
                <span className="text-[9px] text-slate-400" data-id="element-2713">14:02</span>
                <span className="text-[10px] font-bold text-slate-500" data-id="element-2714">
                  {consultation.astrologer}
                </span>
              </div>
              <div className="max-w-[80%] p-3 rounded-2xl text-sm bg-indigo-50 text-indigo-900 rounded-br-md" data-id="element-2715">
                Добрый день! Давайте посмотрим вашу натальную карту.
              </div>
            </div>
            {consultation.hasRisk && <div className="flex flex-col items-start" data-id="element-2716">
                <div className="flex items-baseline gap-2 mb-1" data-id="element-2717">
                  <span className="text-[10px] font-bold text-slate-500" data-id="element-2718">
                    {consultation.user}
                  </span>
                  <span className="text-[9px] text-slate-400" data-id="element-2719">14:05</span>
                </div>
                <div className="max-w-[80%] p-3 rounded-2xl text-sm bg-rose-50 border border-rose-200 text-rose-900 rounded-bl-md" data-id="element-2720">
                  <div className="flex items-center gap-1 text-[10px] font-bold text-rose-600 mb-1" data-id="element-2721">
                    <AlertTriangle className="w-3 h-3" data-id="element-2722" /> Risk Flag: Попытка
                    увода с платформы
                  </div>
                  А можно вам в telegram написать? Там удобнее. Мой номер
                  +79991234567
                </div>
              </div>}
          </div>}

        {activeDetailTab === 'video' && <div className="flex flex-col items-center justify-center py-12 bg-slate-900 rounded-xl text-slate-400 relative overflow-hidden" data-id="element-2723">
            {consultation.hasRecording ? <>
                <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1516321318423-f06f85e504b3?q=80&w=1000&auto=format&fit=crop')] bg-cover bg-center opacity-20 mix-blend-luminosity" data-id="element-2724"></div>
                <PlayCircle className="w-16 h-16 text-white opacity-80 hover:opacity-100 hover:scale-110 transition-all cursor-pointer z-10" data-id="element-2725" />
                <p className="text-sm font-medium mt-4 text-white z-10" data-id="element-2726">
                  Запись консультации ({consultation.actualDuration} мин)
                </p>
                <div className="flex gap-3 mt-6 z-10" data-id="element-2727">
                  <Button variant="primary" size="sm" icon={<PlayCircle className="w-4 h-4" data-id="element-2729" />} data-id="element-2728">
                    Смотреть
                  </Button>
                  <Button variant="outline" size="sm" className="text-white border-white/40 bg-white/10 hover:bg-white/20 backdrop-blur-sm" icon={<Download className="w-4 h-4" data-id="element-2731" />} data-id="element-2730">
                    Скачать (145 MB)
                  </Button>
                </div>
              </> : <>
                <Video className="w-12 h-12 mb-4 opacity-30" data-id="element-2732" />
                <p className="text-sm font-medium" data-id="element-2733">Запись недоступна</p>
                <p className="text-xs mt-1 opacity-60" data-id="element-2734">
                  Консультация еще не завершена или проходила в текстовом
                  формате
                </p>
              </>}
          </div>}

        {activeDetailTab === 'ai' && <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2735">
            <Card padding="sm" className="border-slate-200 bg-white" data-id="element-2736">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-100" data-id="element-2737">
                <Zap className="w-5 h-5 text-violet-500" data-id="element-2738" />
                <h4 className="font-bold text-slate-900" data-id="element-2739">
                  AI Анализ поведения
                </h4>
              </div>
              <div className="space-y-4" data-id="element-2740">
                <div data-id="element-2741">
                  <div className="flex justify-between text-xs mb-1" data-id="element-2742">
                    <span className="text-slate-600" data-id="element-2743">
                      Эмоциональный фон (Sentiment)
                    </span>
                    <span className="font-bold text-emerald-600" data-id="element-2744">
                      Позитивный (85%)
                    </span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-1.5" data-id="element-2745">
                    <div className="bg-emerald-500 h-1.5 rounded-full" style={{
                  width: '85%'
                }} data-id="element-2746"></div>
                  </div>
                </div>
                <div data-id="element-2747">
                  <div className="flex justify-between text-xs mb-1" data-id="element-2748">
                    <span className="text-slate-600" data-id="element-2749">
                      Вовлеченность астролога
                    </span>
                    <span className="font-bold text-indigo-600" data-id="element-2750">
                      Высокая (92%)
                    </span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-1.5" data-id="element-2751">
                    <div className="bg-indigo-500 h-1.5 rounded-full" style={{
                  width: '92%'
                }} data-id="element-2752"></div>
                  </div>
                </div>
                {consultation.hasRisk && <div className="p-3 bg-rose-50 rounded-lg border border-rose-100 mt-4" data-id="element-2753">
                    <div className="flex items-start gap-2" data-id="element-2754">
                      <AlertTriangle className="w-4 h-4 text-rose-600 mt-0.5" data-id="element-2755" />
                      <div data-id="element-2756">
                        <div className="text-xs font-bold text-rose-900" data-id="element-2757">
                          Обнаружены нарушения
                        </div>
                        <div className="text-[10px] text-rose-700 mt-1" data-id="element-2758">
                          AI зафиксировал попытку обмена контактными данными
                          (телефон/telegram) на 5-й минуте.
                        </div>
                      </div>
                    </div>
                  </div>}
              </div>
            </Card>
            <Card padding="sm" className="border-slate-200 bg-white" data-id="element-2759">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-100" data-id="element-2760">
                <FileText className="w-5 h-5 text-blue-500" data-id="element-2761" />
                <h4 className="font-bold text-slate-900" data-id="element-2762">AI Саммари</h4>
              </div>
              <div className="text-sm text-slate-700 space-y-2" data-id="element-2763">
                <p data-id="element-2764">
                  <strong data-id="element-2765">Тема:</strong> Анализ натальной карты, карьерные
                  перспективы.
                </p>
                <p data-id="element-2766">
                  <strong data-id="element-2767">Ключевые моменты:</strong>
                </p>
                <ul className="list-disc pl-4 text-xs space-y-1 text-slate-600" data-id="element-2768">
                  <li data-id="element-2769">Обсуждение перехода в новую сферу деятельности</li>
                  <li data-id="element-2770">Анализ транзитов Сатурна</li>
                  <li data-id="element-2771">
                    Рекомендации по благоприятным датам для запуска проекта
                  </li>
                </ul>
                <p className="text-xs text-slate-500 italic mt-4" data-id="element-2772">
                  Сгенерировано автоматически на основе расшифровки аудио/чата.
                </p>
              </div>
            </Card>
          </div>}

        {activeDetailTab === 'history' && <div className="space-y-0 relative pl-4" data-id="element-2773">
            <div className="absolute left-[23px] top-4 bottom-4 w-px bg-slate-200" data-id="element-2774" />
            {consultation.timeline.map((event: any, idx: number) => <div key={idx} className="flex gap-4 pb-6 relative" data-id="element-2775">
                <div className={`w-3 h-3 rounded-full flex-shrink-0 mt-1 relative z-10 border-2 ${event.type === 'alert' ? 'bg-rose-100 border-rose-500' : event.type === 'system' ? 'bg-slate-100 border-slate-400' : event.type === 'astrologer' ? 'bg-indigo-100 border-indigo-500' : 'bg-emerald-100 border-emerald-500'}`} data-id="element-2776" />
                <div data-id="element-2777">
                  <div className="text-sm font-medium text-slate-900" data-id="element-2778">
                    {event.text}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5" data-id="element-2779">
                    {event.time}
                  </div>
                </div>
              </div>)}
          </div>}
      </div>;
  };
  return <div className="space-y-6 relative" data-id="element-2780">
      <div className="flex justify-between items-center" data-id="element-2781">
        <div data-id="element-2782">
          <h2 className="text-xl font-bold text-slate-900" data-id="element-2783">
            Реестр консультаций
          </h2>
          <p className="text-sm text-slate-500" data-id="element-2784">
            Управление онлайн-сессиями и чатами
          </p>
        </div>
        <Button variant="outline" size="sm" icon={<Download className="w-4 h-4" data-id="element-2786" />} onClick={handleExport} data-id="element-2785">
          Экспорт CSV
        </Button>
      </div>

      {/* KPI Cards (Clickable for filtering) */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-id="element-2787">
        {renderKpiCard('Всего', totalCount, <Video className="w-5 h-5 text-indigo-600" data-id="element-2788" />, 'bg-indigo-50', 'text-slate-900', 'status', 'all')}
        {renderKpiCard('Сегодня', todayCount, <Clock className="w-5 h-5 text-blue-600" data-id="element-2789" />, 'bg-blue-50', 'text-slate-900', 'date', 'today')}
        {renderKpiCard('Онлайн сейчас', activeCount, <Activity className="w-5 h-5 text-emerald-600" data-id="element-2790" />, 'bg-emerald-50', 'text-emerald-600', 'status', 'online')}
        {renderKpiCard('С жалобами', complaintCount, <AlertTriangle className="w-5 h-5 text-amber-600" data-id="element-2791" />, 'bg-amber-50', 'text-amber-600', 'complaints', 'true')}
        {renderKpiCard('Risk Flags', riskCount, <AlertTriangle className="w-5 h-5 text-rose-600" data-id="element-2792" />, 'bg-rose-50', 'text-rose-600', 'riskFlag', 'true')}
      </div>

      {/* Table Section */}
      <Card padding="none" className="rounded-xl shadow-none border-slate-200 overflow-visible" data-id="element-2793">
        <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-slate-50/50" data-id="element-2794">
          <div className="flex items-center gap-3 w-full sm:w-auto flex-1" data-id="element-2795">
            <div className="relative flex-1 max-w-md" data-id="element-2796">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-2797" />
              <input type="text" placeholder="Поиск по ID, имени..." className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 bg-white" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-2798" />
            </div>
            <Button variant={showFilters ? 'primary' : 'outline'} size="sm" icon={<Search className="w-4 h-4" data-id="element-2800" />} onClick={() => setShowFilters(!showFilters)} data-id="element-2799">
              Фильтры
            </Button>
          </div>
        </div>

        {/* Advanced Filters Panel */}
        {showFilters && <div className="p-4 border-b border-slate-100 bg-white space-y-4 animate-in fade-in slide-in-from-top-2" data-id="element-2801">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-2802">
              <div data-id="element-2803">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-2804">
                  Статус
                </label>
                <select className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" value={filters.status} onChange={e => setFilters({
              ...filters,
              status: e.target.value
            })} data-id="element-2805">
                  <option value="all" data-id="element-2806">Все</option>
                  <option value="online" data-id="element-2807">Онлайн сейчас</option>
                  <option value="active" data-id="element-2808">Активные</option>
                  <option value="waiting" data-id="element-2809">Ожидают астролога</option>
                  <option value="scheduled" data-id="element-2810">Запланированные</option>
                  <option value="completed" data-id="element-2811">Завершенные</option>
                  <option value="cancelled" data-id="element-2812">Отмененные</option>
                  <option value="dispute" data-id="element-2813">Спор</option>
                </select>
              </div>
              <div data-id="element-2814">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-2815">
                  Формат
                </label>
                <select className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" value={filters.format} onChange={e => setFilters({
              ...filters,
              format: e.target.value
            })} data-id="element-2816">
                  <option value="all" data-id="element-2817">Все</option>
                  <option value="video" data-id="element-2818">Видеозвонок</option>
                  <option value="audio" data-id="element-2819">Аудиозвонок</option>
                  <option value="chat" data-id="element-2820">Чат</option>
                </select>
              </div>
              <div data-id="element-2821">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-2822">
                  Risk Flag
                </label>
                <select className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" value={filters.riskFlag} onChange={e => setFilters({
              ...filters,
              riskFlag: e.target.value
            })} data-id="element-2823">
                  <option value="all" data-id="element-2824">Все</option>
                  <option value="true" data-id="element-2825">Есть нарушения</option>
                  <option value="false" data-id="element-2826">Нет нарушений</option>
                </select>
              </div>
              <div className="flex items-end" data-id="element-2827">
                <Button variant="ghost" size="sm" onClick={() => setFilters({
              status: 'all',
              format: 'all',
              riskFlag: 'all',
              date: 'all',
              complaints: 'all'
            })} data-id="element-2828">
                  Сбросить
                </Button>
              </div>
            </div>
          </div>}

        <div className="overflow-x-auto overflow-y-visible" data-id="element-2829">
          <table className="w-full text-left" style={{
          minWidth: '1000px'
        }} data-id="element-2830">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 text-[10px] uppercase tracking-wider" data-id="element-2831">
              <tr data-id="element-2832">
                <th className="px-3 py-2.5 font-medium" data-id="element-2833">ID / Дата</th>
                <th className="px-3 py-2.5 font-medium" data-id="element-2834">Тип</th>
                <th className="px-3 py-2.5 font-medium" data-id="element-2835">
                  Пользователь / Астролог
                </th>
                <th className="px-3 py-2.5 font-medium" data-id="element-2836">Стоимость</th>
                <th className="px-3 py-2.5 font-medium" data-id="element-2837">
                  Длительность (План/Факт)
                </th>
                <th className="px-3 py-2.5 font-medium" data-id="element-2838">Последнее сообщение</th>
                <th className="px-3 py-2.5 font-medium" data-id="element-2839">Рейтинг / Запись</th>
                <th className="px-3 py-2.5 font-medium" data-id="element-2840">Статус</th>
                <th className="px-3 py-2.5 text-right font-medium w-10" data-id="element-2841"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-2842">
              {currentConsultations.length === 0 ? <tr data-id="element-2843">
                  <td colSpan={9} className="px-3 py-8 text-center text-slate-500 text-sm" data-id="element-2844">
                    Консультации не найдены
                  </td>
                </tr> : currentConsultations.map(c => <Fragment key={c.id} data-id="element-2845">
                    <tr className={`transition-colors group ${expandedRow === c.id ? 'bg-slate-50' : c.status === 'waiting' ? 'bg-amber-50/30 hover:bg-amber-50/60' : c.hasRisk ? 'bg-rose-50/30 hover:bg-rose-50/60' : 'hover:bg-slate-50'}`} data-id="element-2846">
                      {/* ID & Date */}
                      <td className="px-3 py-2.5" data-id="element-2847">
                        <div className="flex items-center gap-1.5 text-[11px] font-bold text-indigo-600 cursor-pointer hover:text-indigo-800" onClick={() => setExpandedRow(expandedRow === c.id ? null : c.id)} data-id="element-2848">
                          {expandedRow === c.id ? <ChevronUp className="w-3.5 h-3.5 flex-shrink-0" data-id="element-2849" /> : <ChevronDown className="w-3.5 h-3.5 flex-shrink-0" data-id="element-2850" />}
                          <span className="hover:underline" data-id="element-2851">{c.id}</span>
                        </div>
                        <div className="text-[9px] text-slate-500 mt-0.5 ml-5" data-id="element-2852">
                          {c.date}
                        </div>
                        {c.hasRisk && <div className="flex gap-1 mt-1" data-id="element-2853">
                            {c.riskFlags.map((rf: any, idx: number) => <Tooltip key={idx} text={rf.label} data-id="element-2854">
                                <AlertTriangle className="w-3 h-3 text-rose-500 cursor-help" data-id="element-2855" />
                              </Tooltip>)}
                          </div>}
                      </td>

                      {/* Format */}
                      <td className="px-3 py-2.5" data-id="element-2856">
                        <div className="flex items-center gap-1.5" data-id="element-2857">
                          {getFormatIcon(c.format)}
                          <span className="text-[11px] font-medium text-slate-700" data-id="element-2858">
                            {c.format === 'video' ? 'Видео' : c.format === 'audio' ? 'Аудио' : 'Чат'}
                          </span>
                        </div>
                      </td>

                      {/* Users */}
                      <td className="px-3 py-2.5" data-id="element-2859">
                        <div className="text-[11px] font-medium text-slate-900 truncate max-w-[120px]" title={c.user} data-id="element-2860">
                          {c.user}
                        </div>
                        <div className="text-[10px] text-slate-500 truncate max-w-[120px] mt-0.5" title={c.astrologer} data-id="element-2861">
                          {c.astrologer}
                        </div>
                      </td>

                      {/* Price */}
                      <td className="px-3 py-2.5" data-id="element-2862">
                        <div className="text-[11px] font-bold text-slate-900" data-id="element-2863">
                          {c.priceNum.toLocaleString('ru-RU')} ₽
                        </div>
                        <div className="text-[9px] text-emerald-600 mt-0.5" data-id="element-2864">
                          Ком: {c.commission.toLocaleString('ru-RU')} ₽
                        </div>
                      </td>

                      {/* Duration */}
                      <td className="px-3 py-2.5" data-id="element-2865">
                        <div className="text-[10px] text-slate-500" data-id="element-2866">
                          План: {c.plannedDuration} мин
                        </div>
                        <div className={`text-[11px] font-medium mt-0.5 ${c.actualDuration > c.plannedDuration ? 'text-amber-600' : 'text-slate-900'}`} data-id="element-2867">
                          Факт: {c.actualDuration} мин
                        </div>
                      </td>

                      {/* Last Message */}
                      <td className="px-3 py-2.5" data-id="element-2868">
                        <div className="text-[10px] text-slate-600 max-w-[140px] truncate" title={c.lastMessage} data-id="element-2869">
                          {c.lastMessage}
                        </div>
                      </td>

                      {/* Rating & Recording */}
                      <td className="px-3 py-2.5" data-id="element-2870">
                        <div className="flex flex-col gap-1" data-id="element-2871">
                          {c.rating ? <div className="flex items-center gap-1 text-[11px] font-medium" data-id="element-2872">
                              <Star className="w-3 h-3 text-amber-400 fill-amber-400" data-id="element-2873" />{' '}
                              {c.rating}
                            </div> : <span className="text-[10px] text-slate-400" data-id="element-2874">
                              —
                            </span>}
                          {c.hasRecording && <div className="flex items-center gap-1 text-[10px] text-indigo-600 hover:text-indigo-800 cursor-pointer font-medium" onClick={e => {
                      e.stopPropagation();
                      setExpandedRow(c.id);
                      setActiveDetailTab('video');
                    }} data-id="element-2875">
                              <PlayCircle className="w-3 h-3" data-id="element-2876" /> Запись
                            </div>}
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-3 py-2.5" data-id="element-2877">
                        {getStatusBadge(c.status)}
                      </td>

                      {/* Actions */}
                      <td className="px-3 py-2.5 text-right relative" data-id="element-2878">
                        <Button variant="ghost" size="sm" className="w-7 h-7 p-0 rounded-md text-slate-400 hover:text-slate-900 hover:bg-slate-100" icon={<MoreVertical className="w-3.5 h-3.5" data-id="element-2880" />} onClick={e => {
                    e.stopPropagation();
                    setOpenActionMenuId(openActionMenuId === c.id ? null : c.id);
                  }} data-id="element-2879" />
                        {openActionMenuId === c.id && <div className="absolute right-0 top-9 w-48 bg-white rounded-xl shadow-xl border border-slate-100 py-1 z-[9999] animate-in fade-in zoom-in-95" data-id="element-2881">
                            <button className="w-full text-left px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={() => {
                      setExpandedRow(c.id);
                      setActiveDetailTab('info');
                      setOpenActionMenuId(null);
                    }} data-id="element-2882">
                              <Eye className="w-3.5 h-3.5" data-id="element-2883" /> Открыть детали
                            </button>
                            <button className="w-full text-left px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={() => {
                      setExpandedRow(c.id);
                      setActiveDetailTab('chat');
                      setOpenActionMenuId(null);
                    }} data-id="element-2884">
                              <MessageCircle className="w-3.5 h-3.5" data-id="element-2885" /> Открыть
                              чат
                            </button>
                            {c.hasRecording && <button className="w-full text-left px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={() => {
                      setExpandedRow(c.id);
                      setActiveDetailTab('video');
                      setOpenActionMenuId(null);
                    }} data-id="element-2886">
                                <PlayCircle className="w-3.5 h-3.5" data-id="element-2887" /> Смотреть
                                запись
                              </button>}
                            <div className="h-px bg-slate-100 my-1" data-id="element-2888" />
                            <button className="w-full text-left px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={() => {
                      const match = users.find(u => u.name === c.user);
                      if (match) setDetailPanel({
                        type: 'user_full',
                        title: 'Профиль клиента',
                        data: match
                      });
                      setOpenActionMenuId(null);
                    }} data-id="element-2889">
                              <UserX className="w-3.5 h-3.5" data-id="element-2890" /> Профиль клиента
                            </button>
                            <div className="h-px bg-slate-100 my-1" data-id="element-2891" />
                            <button className="w-full text-left px-4 py-2 text-xs text-rose-600 hover:bg-rose-50 flex items-center gap-2" onClick={() => {
                      success(`Консультация ${c.id} отменена`);
                      setOpenActionMenuId(null);
                    }} data-id="element-2892">
                              <Ban className="w-3.5 h-3.5" data-id="element-2893" /> Отменить
                            </button>
                            <button className="w-full text-left px-4 py-2 text-xs text-rose-600 hover:bg-rose-50 flex items-center gap-2" onClick={() => {
                      success(`Оформлен возврат по ${c.id}`);
                      setOpenActionMenuId(null);
                    }} data-id="element-2894">
                              <Undo2 className="w-3.5 h-3.5" data-id="element-2895" /> Вернуть деньги
                            </button>
                          </div>}
                      </td>
                    </tr>

                    {/* Expanded Details Row */}
                    {expandedRow === c.id && <tr data-id="element-2896">
                        <td colSpan={9} className="p-0 border-b-2 border-indigo-100" data-id="element-2897">
                          {renderDetailTabs(c)}
                        </td>
                      </tr>}
                  </Fragment>)}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && <div className="p-4 border-t border-slate-100 flex justify-center" data-id="element-2898">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} data-id="element-2899" />
          </div>}
      </Card>

      {/* Video Call Modal — Admin Enhanced */}
      <VideoCallModal isOpen={showCallModal} onClose={() => setShowCallModal(false)} mode={callMode} participantName={callParticipant.name} participantInitials={callParticipant.initials} onEnd={() => success('Звонок завершен. Запись сохранена.')} isAdmin={true} participants={(() => {
      const expanded = expandedRow ? enrichedConsultations.find(c => c.id === expandedRow) : null;
      if (!expanded) return undefined;
      return [{
        name: expanded.user,
        role: 'Клиент',
        initials: expanded.user.charAt(0)
      }, {
        name: expanded.astrologer,
        role: 'Астролог',
        initials: expanded.astrologer.charAt(0)
      }];
    })()} data-id="element-2900" />
    </div>;
}